// ============================================
// COSMIC HINDU - Shop Page (Live API)
// ============================================

import { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { categories, getCategoryById } from '@data/categories';
import ProductCard from '@components/product/ProductCard';

const API = import.meta.env.VITE_API_URL || '/api';

export default function ShopPage() {
  const { categoryId } = useParams();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('search') || '';

  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ limit: 500 });
    if (categoryId) params.set('category', categoryId);
    if (searchQuery) params.set('search', searchQuery);
    fetch(`${API}/get-products.php?${params}`)
      .then(r => r.json())
      .then(d => setAllProducts(d.data || d.products || []))
      .catch(() => setAllProducts([]))
      .finally(() => setLoading(false));
  }, [categoryId, searchQuery]);

  const currentCat = categoryId ? getCategoryById(categoryId) : null;

  return (
    <section className="py-10 min-h-screen">
      <div className="max-w-7xl mx-auto px-5">
        {/* Breadcrumb */}
        <div className="text-sm text-white/40 mb-6">
          <Link to="/" className="text-white/40 no-underline hover:text-gold-500">Home</Link>
          <span className="mx-2">/</span>
          <span className="text-gold-500">{currentCat ? currentCat.name : 'All Products'}</span>
        </div>

        {/* Category filters */}
        <div className="flex gap-3 flex-wrap mb-10">
          <Link to="/shop" className={`btn-outline no-underline py-2 px-5 text-[11px] ${!categoryId ? 'bg-gold-500/15 border-gold-500' : ''}`}>
            All Products
          </Link>
          {categories.map((cat) => (
            <Link key={cat.id} to={`/shop/${cat.id}`}
              className={`btn-outline no-underline py-2 px-5 text-[11px] ${categoryId === cat.id ? 'bg-gold-500/15 border-gold-500' : ''}`}>
              {cat.icon} {cat.name}
            </Link>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-16 text-white/40">
            <div className="text-4xl mb-4">🌀</div>
            <div className="text-sm tracking-widest" style={{ fontFamily: "'Cinzel',serif" }}>Loading products…</div>
          </div>
        ) : (
          <>
            {/* Results count */}
            <div className="text-white/50 text-sm mb-5">
              Showing {allProducts.length} product{allProducts.length !== 1 ? 's' : ''}
              {currentCat && ` in ${currentCat.name}`}
              {searchQuery && ` for "${searchQuery}"`}
            </div>

            {/* Products grid */}
            {allProducts.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {allProducts.map((p) => (
                  <ProductCard key={p.id} product={p} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16 text-white/40">
                <div className="text-5xl mb-4">🔍</div>
                <div className="text-lg">No products found</div>
                <Link to="/shop" className="btn-outline no-underline mt-6 inline-block">View All Products</Link>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}
